/** @module @liquidefy/code-editor/adapters/adapter-dap @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  DAPClientConfig,
  DAPBreakpoint,
  DAPScope,
  DAPStackFrame,
  DAPVariable,
  ExternalToolConfig,
  LanguageId,
} from '../code-editor-types.js';

import {
  ok,
  err,
  now,
  asLanguageId,
} from '../code-editor.js';

// ═══ §2 SESSION MANAGEMENT — DAP launch/attach configuration ═══

export const DEFAULT_LAUNCH_CONFIG: Omit<DAPClientConfig, 'type'> = {
  request: 'launch',
  program: '',
  args: [],
  cwd: '.',
  env: {},
};

export const DEFAULT_ATTACH_CONFIG: Omit<DAPClientConfig, 'type'> = {
  request: 'attach',
  port: 9229,
};

export function createDAPConfig(
  type: string,
  request: 'launch' | 'attach',
  options?: Partial<DAPClientConfig>,
): DAPClientConfig {
  return {
    type,
    request,
    program: options?.program,
    args: options?.args ?? [],
    cwd: options?.cwd ?? '.',
    env: options?.env ?? {},
    port: options?.port,
  };
}

export function buildLaunchConfig(
  program: string,
  args?: string[],
  cwd?: string,
  env?: Record<string, string>,
): DAPClientConfig {
  return {
    type: 'node',
    request: 'launch',
    program,
    args: args ?? [],
    cwd: cwd ?? '.',
    env: env ?? {},
  };
}

export function buildAttachConfig(
  port?: number,
  host?: string,
): DAPClientConfig {
  return {
    type: 'node',
    request: 'attach',
    port: port ?? 9229,
    env: host ? { DAP_HOST: host } : {},
  };
}

export function validateDAPConfig(config: DAPClientConfig): string[] {
  const errors: string[] = [];
  if (!config.type) errors.push('type is required');
  if (config.request !== 'launch' && config.request !== 'attach') {
    errors.push('request must be "launch" or "attach"');
  }
  if (config.request === 'launch' && !config.program) {
    errors.push('program is required for launch configurations');
  }
  if (config.request === 'attach' && !config.port) {
    errors.push('port is recommended for attach configurations');
  }
  if (config.port !== undefined && (config.port < 0 || config.port > 65535)) {
    errors.push('port must be between 0 and 65535');
  }
  return errors;
}

// ═══ §3 BREAKPOINT MAPPING — create, convert, query breakpoints ═══

export function createBreakpoint(
  uri: string,
  line: number,
  options?: {
    column?: number;
    condition?: string;
    hitCondition?: string;
    logMessage?: string;
  },
): DAPBreakpoint {
  return {
    id: `bp_${uri}_${line}_${now()}`,
    location: { uri, line, column: options?.column },
    condition: options?.condition,
    hitCondition: options?.hitCondition,
    logMessage: options?.logMessage,
    isVerified: false,
  };
}

export function breakpointToDAP(bp: DAPBreakpoint): Record<string, unknown> {
  return {
    id: bp.id,
    source: { path: bp.location.uri },
    line: bp.location.line,
    column: bp.location.column,
    condition: bp.condition,
    hitCondition: bp.hitCondition,
    logMessage: bp.logMessage,
    verified: bp.isVerified,
  };
}

export function breakpointFromDAP(dapBp: Record<string, unknown>): DAPBreakpoint {
  const source = dapBp['source'] as Record<string, unknown> | undefined;
  return {
    id: (dapBp['id'] as string) ?? `bp_${now()}`,
    location: {
      uri: (source?.['path'] as string) ?? '',
      line: (dapBp['line'] as number) ?? 0,
      column: dapBp['column'] as number | undefined,
    },
    condition: dapBp['condition'] as string | undefined,
    hitCondition: dapBp['hitCondition'] as string | undefined,
    logMessage: dapBp['logMessage'] as string | undefined,
    isVerified: (dapBp['verified'] as boolean) ?? false,
  };
}

export function isConditionalBreakpoint(bp: DAPBreakpoint): boolean {
  return bp.condition !== undefined && bp.condition !== '';
}

export function isLogPoint(bp: DAPBreakpoint): boolean {
  return bp.logMessage !== undefined && bp.logMessage !== '';
}

export function isHitCountBreakpoint(bp: DAPBreakpoint): boolean {
  return bp.hitCondition !== undefined && bp.hitCondition !== '';
}

export function breakpointKey(bp: DAPBreakpoint): string {
  return `${bp.location.uri}:${bp.location.line}${bp.location.column !== undefined ? ':' + bp.location.column : ''}`;
}

export function sortBreakpoints(breakpoints: DAPBreakpoint[]): DAPBreakpoint[] {
  return [...breakpoints].sort((a, b) => {
    const uriCmp = a.location.uri.localeCompare(b.location.uri);
    if (uriCmp !== 0) return uriCmp;
    const lineCmp = a.location.line - b.location.line;
    if (lineCmp !== 0) return lineCmp;
    return (a.location.column ?? 0) - (b.location.column ?? 0);
  });
}

export function breakpointsForFile(breakpoints: DAPBreakpoint[], uri: string): DAPBreakpoint[] {
  return breakpoints.filter(bp => bp.location.uri === uri);
}

export function breakpointAtLine(
  breakpoints: DAPBreakpoint[],
  uri: string,
  line: number,
): DAPBreakpoint | undefined {
  return breakpoints.find(bp => bp.location.uri === uri && bp.location.line === line);
}

// ═══ §4 STACK FRAME MAPPING — stack frames, locations, filtering ═══

export function stackFrameFromDAP(dapFrame: Record<string, unknown>): DAPStackFrame {
  const source = dapFrame['source'] as Record<string, unknown> | undefined;
  const scopes = (dapFrame['scopes'] as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: (dapFrame['id'] as number) ?? 0,
    name: (dapFrame['name'] as string) ?? '<unknown>',
    source: {
      uri: (source?.['path'] as string) ?? (source?.['name'] as string) ?? '<unknown>',
      line: (dapFrame['line'] as number) ?? 0,
    },
    line: (dapFrame['line'] as number) ?? 0,
    column: (dapFrame['column'] as number) ?? 0,
    scopes: scopes.map(scopeFromDAP),
  };
}

export function formatStackFrame(frame: DAPStackFrame): string {
  const sourceName = frame.source.uri.split('/').pop() ?? frame.source.uri;
  return `${frame.name} (${sourceName}:${frame.line}:${frame.column})`;
}

export function stackFrameLocation(frame: DAPStackFrame): { uri: string; line: number; column: number } {
  return {
    uri: frame.source.uri,
    line: frame.line,
    column: frame.column,
  };
}

export function isExternalFrame(frame: DAPStackFrame): boolean {
  const uri = frame.source.uri;
  return uri.includes('node_modules') ||
    uri.includes('node:') ||
    uri.includes('<node_internals>') ||
    uri.startsWith('internal/') ||
    uri.includes('.cargo/registry') ||
    uri.includes('site-packages');
}

export function isUserCode(frame: DAPStackFrame, workspacePaths: string[]): boolean {
  if (isExternalFrame(frame)) return false;
  if (workspacePaths.length === 0) return true;
  return workspacePaths.some(wp => frame.source.uri.startsWith(wp));
}

export function stackTraceToString(frames: DAPStackFrame[]): string {
  return frames.map((f, i) => `  #${i} ${formatStackFrame(f)}`).join('\n');
}

export function topFrame(frames: DAPStackFrame[]): DAPStackFrame | undefined {
  return frames.length > 0 ? frames[0] : undefined;
}

export function filterToUserCode(
  frames: DAPStackFrame[],
  workspacePaths: string[],
): DAPStackFrame[] {
  return frames.filter(f => isUserCode(f, workspacePaths));
}

// ═══ §5 VARIABLE MAPPING — variables, display, filtering ═══

export const PRIMITIVE_TYPES: readonly string[] = [
  'string', 'number', 'boolean', 'null', 'undefined',
  'int', 'float', 'double', 'char', 'byte', 'short', 'long',
  'i8', 'i16', 'i32', 'i64', 'u8', 'u16', 'u32', 'u64',
  'f32', 'f64', 'bool', 'str', 'usize', 'isize',
  'int8', 'int16', 'int32', 'int64', 'uint8', 'uint16', 'uint32', 'uint64',
] as const;

export function variableFromDAP(dapVar: Record<string, unknown>): DAPVariable {
  return {
    name: (dapVar['name'] as string) ?? '',
    value: (dapVar['value'] as string) ?? '',
    type: dapVar['type'] as string | undefined,
    variablesReference: (dapVar['variablesReference'] as number) ?? 0,
    namedVariables: dapVar['namedVariables'] as number | undefined,
    indexedVariables: dapVar['indexedVariables'] as number | undefined,
  };
}

export function formatVariable(variable: DAPVariable): string {
  const typeStr = variable.type ? `: ${variable.type}` : '';
  return `${variable.name}${typeStr} = ${variable.value}`;
}

export function isExpandable(variable: DAPVariable): boolean {
  return variable.variablesReference > 0;
}

export function isPrimitive(variable: DAPVariable): boolean {
  if (!variable.type) return variable.variablesReference === 0;
  return PRIMITIVE_TYPES.includes(variable.type.toLowerCase());
}

export function variableTypeName(variable: DAPVariable): string {
  if (variable.type) return variable.type;
  if (variable.variablesReference > 0) return 'object';
  const val = variable.value;
  if (val === 'true' || val === 'false') return 'boolean';
  if (val === 'null') return 'null';
  if (val === 'undefined') return 'undefined';
  if (!isNaN(Number(val)) && val !== '') return 'number';
  if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) return 'string';
  return 'unknown';
}

export function createVariableFilter(query: string): (v: DAPVariable) => boolean {
  const lower = query.toLowerCase();
  return (v: DAPVariable) =>
    v.name.toLowerCase().includes(lower) ||
    v.value.toLowerCase().includes(lower) ||
    (v.type?.toLowerCase().includes(lower) ?? false);
}

export function filterVariables(
  variables: DAPVariable[],
  filter: (v: DAPVariable) => boolean,
): DAPVariable[] {
  return variables.filter(filter);
}

// ═══ §6 SCOPE MAPPING — scopes, formatting, priority ═══

export const STANDARD_SCOPES: readonly string[] = ['Locals', 'Globals', 'Closure'] as const;

const SCOPE_PRIORITY_MAP: ReadonlyMap<string, number> = new Map([
  ['Locals', 0],
  ['Arguments', 1],
  ['Closure', 2],
  ['Block', 3],
  ['Module', 4],
  ['Globals', 5],
  ['Registers', 6],
]);

export function scopeFromDAP(dapScope: Record<string, unknown>): DAPScope {
  return {
    name: (dapScope['name'] as string) ?? 'Unknown',
    variablesReference: (dapScope['variablesReference'] as number) ?? 0,
    expensive: (dapScope['expensive'] as boolean) ?? false,
  };
}

export function formatScope(scope: DAPScope): string {
  const expStr = scope.expensive ? ' (expensive)' : '';
  return `${scope.name}${expStr} [ref=${scope.variablesReference}]`;
}

export function isExpensiveScope(scope: DAPScope): boolean {
  return scope.expensive;
}

export function scopePriority(scope: DAPScope): number {
  return SCOPE_PRIORITY_MAP.get(scope.name) ?? 99;
}

// ═══ §7 DEBUG COMMANDS — build DAP request payloads for stepping/evaluation ═══

export function buildStepOverCommand(): Record<string, unknown> {
  return { command: 'next', type: 'request' };
}

export function buildStepIntoCommand(): Record<string, unknown> {
  return { command: 'stepIn', type: 'request' };
}

export function buildStepOutCommand(): Record<string, unknown> {
  return { command: 'stepOut', type: 'request' };
}

export function buildContinueCommand(threadId: number): Record<string, unknown> {
  return { command: 'continue', type: 'request', arguments: { threadId } };
}

export function buildPauseCommand(threadId: number): Record<string, unknown> {
  return { command: 'pause', type: 'request', arguments: { threadId } };
}

export function buildRestartCommand(): Record<string, unknown> {
  return { command: 'restart', type: 'request' };
}

export function buildDisconnectCommand(restart?: boolean): Record<string, unknown> {
  return {
    command: 'disconnect',
    type: 'request',
    arguments: { restart: restart ?? false, terminateDebuggee: true },
  };
}

export function buildEvaluateCommand(
  expression: string,
  frameId?: number,
  context?: 'watch' | 'repl' | 'hover' | 'clipboard',
): Record<string, unknown> {
  return {
    command: 'evaluate',
    type: 'request',
    arguments: {
      expression,
      frameId,
      context: context ?? 'repl',
    },
  };
}

export function buildSetVariableCommand(
  variablesRef: number,
  name: string,
  value: string,
): Record<string, unknown> {
  return {
    command: 'setVariable',
    type: 'request',
    arguments: { variablesReference: variablesRef, name, value },
  };
}

export function buildStackTraceCommand(
  threadId: number,
  startFrame?: number,
  levels?: number,
): Record<string, unknown> {
  return {
    command: 'stackTrace',
    type: 'request',
    arguments: {
      threadId,
      startFrame: startFrame ?? 0,
      levels: levels ?? 20,
    },
  };
}

// ═══ §8 EXTERNAL TOOLS — tool configs for formatters, linters, external integrations ═══

export const COMMON_FORMATTERS: Record<string, Partial<ExternalToolConfig>> = {
  prettier: {
    name: 'prettier',
    command: 'npx',
    args: ['prettier', '--write'],
    formatOnSave: true,
    lintOnSave: false,
  },
  black: {
    name: 'black',
    command: 'black',
    args: [],
    formatOnSave: true,
    lintOnSave: false,
  },
  gofmt: {
    name: 'gofmt',
    command: 'gofmt',
    args: ['-w'],
    formatOnSave: true,
    lintOnSave: false,
  },
  rustfmt: {
    name: 'rustfmt',
    command: 'rustfmt',
    args: [],
    formatOnSave: true,
    lintOnSave: false,
  },
  clangformat: {
    name: 'clang-format',
    command: 'clang-format',
    args: ['-i'],
    formatOnSave: true,
    lintOnSave: false,
  },
  dartformat: {
    name: 'dart-format',
    command: 'dart',
    args: ['format'],
    formatOnSave: true,
    lintOnSave: false,
  },
  swiftformat: {
    name: 'swift-format',
    command: 'swift-format',
    args: ['-i'],
    formatOnSave: true,
    lintOnSave: false,
  },
  scalafmt: {
    name: 'scalafmt',
    command: 'scalafmt',
    args: [],
    formatOnSave: true,
    lintOnSave: false,
  },
};

export const COMMON_LINTERS: Record<string, Partial<ExternalToolConfig>> = {
  eslint: {
    name: 'eslint',
    command: 'npx',
    args: ['eslint', '--fix'],
    formatOnSave: false,
    lintOnSave: true,
  },
  pylint: {
    name: 'pylint',
    command: 'pylint',
    args: [],
    formatOnSave: false,
    lintOnSave: true,
  },
  clippy: {
    name: 'clippy',
    command: 'cargo',
    args: ['clippy'],
    formatOnSave: false,
    lintOnSave: true,
  },
  golint: {
    name: 'golangci-lint',
    command: 'golangci-lint',
    args: ['run'],
    formatOnSave: false,
    lintOnSave: true,
  },
  rubocop: {
    name: 'rubocop',
    command: 'rubocop',
    args: ['-a'],
    formatOnSave: false,
    lintOnSave: true,
  },
  shellcheck: {
    name: 'shellcheck',
    command: 'shellcheck',
    args: [],
    formatOnSave: false,
    lintOnSave: true,
  },
  stylelint: {
    name: 'stylelint',
    command: 'npx',
    args: ['stylelint', '--fix'],
    formatOnSave: false,
    lintOnSave: true,
  },
  flake8: {
    name: 'flake8',
    command: 'flake8',
    args: [],
    formatOnSave: false,
    lintOnSave: true,
  },
  mypy: {
    name: 'mypy',
    command: 'mypy',
    args: [],
    formatOnSave: false,
    lintOnSave: true,
  },
};

export function createExternalToolConfig(
  name: string,
  command: string,
  options?: {
    args?: string[];
    languages?: string[];
    formatOnSave?: boolean;
    lintOnSave?: boolean;
  },
): ExternalToolConfig {
  return {
    name,
    command,
    args: options?.args ?? [],
    languages: (options?.languages ?? []).map(l => asLanguageId(l)),
    formatOnSave: options?.formatOnSave ?? false,
    lintOnSave: options?.lintOnSave ?? false,
  };
}

export function toolCommandForFile(config: ExternalToolConfig, filePath: string): string[] {
  return [config.command, ...config.args, filePath];
}

export function shouldRunOnSave(config: ExternalToolConfig, action: 'format' | 'lint'): boolean {
  if (action === 'format') return config.formatOnSave;
  return config.lintOnSave;
}

export function isFormatterTool(config: ExternalToolConfig): boolean {
  return config.formatOnSave;
}

export function isLinterTool(config: ExternalToolConfig): boolean {
  return config.lintOnSave;
}
